/**
 * Camada de acesso a dados do Elo (Fase 1, local). Leituras para `useLiveQuery`;
 * escritas seguem a spec: a nota é editável na F1; quando há alimento + degrau
 * observado, o resultado grava uma `Oferta(origem=profissional)` reaproveitando
 * `registrarOfertaProfissional` do módulo base — a escada se move sem código novo.
 */
import { db } from "../schema.dexie";
import { registrarOfertaProfissional } from "../db/queries";
import type { EspecialidadeProf, PassoEscada } from "../types";
import { resultadoGeraOferta } from "./domain";
import type {
  AnexoClinico,
  NotaSessao,
  Profissional,
} from "./types";

const novoId = () => crypto.randomUUID();
const agoraISO = () => new Date().toISOString();

// ── Profissionais (biblioteca) ────────────────────────────────────────────

export const listarProfissionais = (incluirArquivados = false): Promise<Profissional[]> =>
  db.profissional
    .orderBy("nome")
    .filter((p) => incluirArquivados || !p.arquivado)
    .toArray();

export const getProfissional = (id: string) => db.profissional.get(id);

export type NovoProfissionalInput = {
  nome: string;
  especialidade: EspecialidadeProf;
  registro_classe?: string | null;
  contato?: string | null;
};

export async function criarProfissional(input: NovoProfissionalInput): Promise<string> {
  const p: Profissional = {
    id: novoId(),
    nome: input.nome.trim(),
    especialidade: input.especialidade,
    registro_classe: input.registro_classe?.trim() || null,
    contato: input.contato?.trim() || null,
    arquivado: false,
    criado_em: agoraISO(),
  };
  await db.profissional.add(p);
  return p.id;
}

export const editarProfissional = (id: string, patch: Partial<Profissional>) =>
  db.profissional.update(id, patch);

export const arquivarProfissional = (id: string) =>
  db.profissional.update(id, { arquivado: true });

// ── Notas de sessão (evento) ──────────────────────────────────────────────

export const getNotasDaCrianca = (criancaId: string): Promise<NotaSessao[]> =>
  db.nota
    .where("[crianca_id+data_sessao]")
    .between([criancaId, ""], [criancaId, "￿"])
    .reverse()
    .toArray();

export const getNotasDoProfissional = (profissionalId: string): Promise<NotaSessao[]> =>
  db.nota
    .where("profissional_id")
    .equals(profissionalId)
    .reverse()
    .sortBy("data_sessao");

export const getNota = (id: string) => db.nota.get(id);

export const getTodasNotas = (): Promise<NotaSessao[]> => db.nota.toArray();

export type NovaNotaInput = {
  crianca_id: string;
  profissional_id: string;
  especialidade: EspecialidadeProf;
  data_sessao?: string; // padrão: agora
  objetivo: string;
  alimento_id?: string | null;
  degrau_alvo?: PassoEscada | null;
  degrau_observado?: PassoEscada | null;
  atividades?: string[];
  resultados?: string | null;
  orientacoes_casa?: string | null;
  observacoes?: string | null;
  anexos?: AnexoClinico[];
};

/**
 * Registra uma sessão. Se houver alimento + degrau observado, grava também uma
 * Oferta(origem=profissional) e guarda o id para rastreio (oferta_id_gerada).
 */
export async function criarNotaSessao(input: NovaNotaInput): Promise<string> {
  const id = novoId();

  let oferta_id_gerada: string | null = null;
  if (
    resultadoGeraOferta({
      alimento_id: input.alimento_id ?? null,
      degrau_observado: input.degrau_observado ?? null,
    })
  ) {
    oferta_id_gerada = await registrarOfertaProfissional({
      alimento_id: input.alimento_id!,
      crianca_id: input.crianca_id,
      passo_escada: input.degrau_observado!,
      profissional_nome: (await db.profissional.get(input.profissional_id))?.nome ?? null,
      profissional_especialidade: input.especialidade,
    });
  }

  const nota: NotaSessao = {
    id,
    crianca_id: input.crianca_id,
    profissional_id: input.profissional_id,
    especialidade: input.especialidade,
    data_sessao: input.data_sessao ?? agoraISO(),
    objetivo: input.objetivo.trim(),
    alimento_id: input.alimento_id ?? null,
    degrau_alvo: input.degrau_alvo ?? null,
    degrau_observado: input.degrau_observado ?? null,
    atividades: input.atividades ?? [],
    resultados: input.resultados?.trim() || null,
    orientacoes_casa: input.orientacoes_casa?.trim() || null,
    orientacao_feita_em: null,
    observacoes: input.observacoes?.trim() || null,
    anexos: input.anexos ?? [],
    oferta_id_gerada,
    estado: "publicada", // F1: editável; rascunho/versão = F2
    versao_de: null,
    criado_em: agoraISO(),
  };
  await db.nota.add(nota);
  return id;
}

export const editarNota = (id: string, patch: Partial<NotaSessao>) =>
  db.nota.update(id, patch);

/**
 * Marca/desmarca a orientação de uma nota como "feita em casa" (toggle do cuidador).
 * Guarda QUANDO foi marcada (ISO) — null quando desmarca. P0.4b.
 */
export const marcarOrientacaoFeita = (id: string, feita: boolean) =>
  db.nota.update(id, { orientacao_feita_em: feita ? agoraISO() : null });

export const excluirNota = (id: string) => db.nota.delete(id);
